import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";
import { Clapperboard, Video, Megaphone, CalendarDays, Briefcase, Rocket, Sparkles, ArrowRight, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const BUSINESS_TYPES = [
  { key: "film", label: "Film & TV production", icon: Clapperboard, template: "film-tv" },
  { key: "video", label: "Video / content", icon: Video, template: "video-production" },
  { key: "advertising", label: "Advertising / agency", icon: Megaphone, template: "ad-campaign" },
  { key: "event", label: "Events / festivals", icon: CalendarDays, template: "event-festival" },
  { key: "product", label: "Product / launch", icon: Rocket, template: "product-launch" },
  { key: "client", label: "Consulting / client work", icon: Briefcase, template: "client-job" },
];

const TEAM_SIZES = ["Solo", "2 – 5", "6 – 20", "20 +"];

export default function Onboarding() {
  const nav = useNavigate();
  const { settings, refresh } = useSettings() || {};
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    business_name: "",
    business_type: "",
    team_size: "Solo",
    current_focus: "",
    seed_demo_project: true,
    seed_demo_contacts: true,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // If already onboarded, bounce straight to Today
  useEffect(() => {
    if (settings?.onboarded) nav("/", { replace: true });
    // If they arrive without a business_name in settings, prefill from settings.product_name for editing continuity
    if (settings?.business_name && !form.business_name) {
      setForm((f) => ({ ...f, business_name: settings.business_name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const canNext = () => {
    if (step === 0) return !!form.business_name.trim();
    if (step === 1) return !!form.business_type;
    if (step === 2) return true;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/complete", form);
      setResult(data);
      setStep(4);
      if (refresh) await refresh();
      toast.success("Mahano OS is set up — welcome aboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed. Try again.");
    } finally { setBusy(false); }
  };

  const goToApp = () => nav("/", { replace: true });

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-10" data-testid="onboarding-page">
      <div className="flex items-center gap-3 mb-10">
        <HaloRing size={30} />
        <HaloWordmark height={22} />
      </div>

      <div className="w-full max-w-2xl">
        <StepIndicator step={step} />

        <Card className="studio-card p-8 mt-4">
          {step === 0 && <StepBusinessName form={form} setForm={setForm} />}
          {step === 1 && <StepBusinessType form={form} setForm={setForm} />}
          {step === 2 && <StepFocus form={form} setForm={setForm} />}
          {step === 3 && <StepReview form={form} setForm={setForm} />}
          {step === 4 && <StepDone result={result} onDone={goToApp} />}

          {step < 4 && (
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="onboarding-back"
              >
                ← Back
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(3, s + 1))}
                  disabled={!canNext()}
                  data-testid="onboarding-next"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={busy} data-testid="onboarding-finish">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up…</> : <><Sparkles className="w-4 h-4 mr-2" /> Set up Mahano OS</>}
                </Button>
              )}
            </div>
          )}
        </Card>

        {step < 4 && (
          <div className="text-center mt-6">
            <button
              onClick={goToApp}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid="onboarding-skip"
            >
              Skip the walkthrough — I'll set things up myself
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  const total = 4;
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1 flex-1 rounded-full transition-colors",
            i <= step ? "bg-[hsl(var(--studio-primary))]" : "bg-border"
          )}
        />
      ))}
    </div>
  );
}

function StepBusinessName({ form, setForm }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--studio-primary))]">Step 1 of 4</div>
        <h2 className="text-2xl font-semibold tracking-tight mt-1">Let's set up your Mahano OS.</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Mahano OS is your studio operating system. Start with your name so we can put it everywhere.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Business or studio name</Label>
        <Input
          data-testid="onboarding-business-name"
          autoFocus
          placeholder="Shadow Wolves Productions"
          value={form.business_name}
          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
          className="text-lg h-11"
        />
      </div>
    </div>
  );
}

function StepBusinessType({ form, setForm }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--studio-primary))]">Step 2 of 4</div>
        <h2 className="text-2xl font-semibold tracking-tight mt-1">What kind of work does {form.business_name || "your business"} do?</h2>
        <p className="text-sm text-muted-foreground mt-2">
          We'll pre-load templates and demo data for the closest match. Pick one — you can always customise later.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {BUSINESS_TYPES.map((t) => {
          const Icon = t.icon;
          const active = form.business_type === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setForm({ ...form, business_type: t.key })}
              data-testid={`onboarding-type-${t.key}`}
              className={cn(
                "text-left p-4 rounded-xl border-2 transition-all",
                active
                  ? "border-[hsl(var(--studio-primary))] bg-[hsl(var(--studio-primary)/0.05)]"
                  : "border-border hover:border-foreground/25"
              )}
            >
              <Icon className={cn("w-6 h-6", active ? "text-[hsl(var(--studio-primary))]" : "text-muted-foreground")} strokeWidth={1.75} />
              <div className="mt-2 font-medium">{t.label}</div>
              {active && <div className="text-[11px] text-[hsl(var(--studio-primary))] mt-1">✓ Will seed with the <b>{t.label}</b> template</div>}
            </button>
          );
        })}
      </div>
      <div className="space-y-1.5 pt-2">
        <Label className="text-xs">Team size</Label>
        <div className="flex gap-2">
          {TEAM_SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setForm({ ...form, team_size: s })}
              data-testid={`onboarding-team-${s.replace(/\s+/g, "").replace("+", "-plus")}`}
              className={cn(
                "flex-1 text-xs px-3 py-2 rounded-md border transition-colors",
                form.team_size === s ? "border-[hsl(var(--studio-primary))] bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))]" : "border-border text-muted-foreground hover:border-foreground/25"
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StepFocus({ form, setForm }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--studio-primary))]">Step 3 of 4</div>
        <h2 className="text-2xl font-semibold tracking-tight mt-1">What's on your plate right now?</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Describe one current or upcoming project in a sentence — Mahano will use it to name your first demo project.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Current focus</Label>
        <Textarea
          rows={3}
          data-testid="onboarding-focus"
          placeholder="e.g. Shooting a short doc about regional music venues, due to premiere at MIFF."
          value={form.current_focus}
          onChange={(e) => setForm({ ...form, current_focus: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[hsl(var(--studio-primary))]"
            checked={form.seed_demo_project}
            onChange={(e) => setForm({ ...form, seed_demo_project: e.target.checked })}
            data-testid="onboarding-seed-project"
          />
          <div>
            <div className="text-sm font-medium">Seed a starter project with stages, tasks & folders</div>
            <div className="text-xs text-muted-foreground">Recommended — you can rename or delete anything.</div>
          </div>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 accent-[hsl(var(--studio-primary))]"
            checked={form.seed_demo_contacts}
            onChange={(e) => setForm({ ...form, seed_demo_contacts: e.target.checked })}
            data-testid="onboarding-seed-contacts"
          />
          <div>
            <div className="text-sm font-medium">Add a couple of sample people & a sample company</div>
            <div className="text-xs text-muted-foreground">So you can see how the CRM feels — flagged with a "demo" tag.</div>
          </div>
        </label>
      </div>
    </div>
  );
}

function StepReview({ form }) {
  const t = BUSINESS_TYPES.find((x) => x.key === form.business_type);
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--studio-primary))]">Step 4 of 4</div>
        <h2 className="text-2xl font-semibold tracking-tight mt-1">Ready?</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Here's what Mahano will do when you click Set up.
        </p>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border" data-testid="onboarding-review">
        <Row label="Business name" value={form.business_name} />
        <Row label="Industry" value={t?.label || "—"} />
        <Row label="Team size" value={form.team_size || "—"} />
        <Row label="Current focus" value={form.current_focus || "—"} />
        <Row label="Template" value={t ? `Auto-apply "${t.label}"` : "None"} />
        <Row label="Demo project" value={form.seed_demo_project ? "Yes" : "No"} />
        <Row label="Demo contacts" value={form.seed_demo_contacts ? "Yes" : "No"} />
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="grid grid-cols-[140px_1fr] gap-4 px-4 py-3 text-sm">
    <div className="text-muted-foreground text-xs uppercase tracking-wider">{label}</div>
    <div>{value}</div>
  </div>
);

function StepDone({ result, onDone }) {
  return (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 rounded-full bg-[hsl(var(--halo-teal)/0.15)] flex items-center justify-center mx-auto">
        <Check className="w-8 h-8 text-[hsl(var(--halo-teal))]" strokeWidth={2.5} />
      </div>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">You're all set.</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          {result?.template_name && <>We've pre-loaded the <b>{result.template_name}</b> template — </>}
          {result?.tasks_created > 0 && <>{result.tasks_created} tasks and </>}
          {result?.folders_created > 0 && <>{result.folders_created} folders ready to go. </>}
          Start with the morning brief on <b>Today</b>.
        </p>
      </div>
      <Button size="lg" onClick={onDone} data-testid="onboarding-done">
        Open Mahano OS →
      </Button>
    </div>
  );
}
